import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { adobeAnalyticsScriptsSelector } from '@doda/common/src/redux/features/analytics';
import { useEffect } from 'react';
import { sc, tlReadyExecute } from '@doda/common/src/utils/adobeAnalyticsUtil';
import { dataLayer } from '@doda/common/src/utils/googleTagManagerUtil';
import {
  JobListResponse,
  TopicJobResponse,
  JobListMetaDataResponse,
} from '@doda/api-client/aspida/@types';

interface UseFeaturedJobListAnalyticsArgs {
  featuredId: string;
  totalCount: JobListMetaDataResponse['totalCount'];
  topicTitle: TopicJobResponse['title'];
  jobList: JobListResponse['results'];
}

/**
 * 特集枠求人一覧の計測処理を担当するフック
 * @param param.featuredId - 特集枠ID
 * @param param.totalCount - 検索条件に当てはまる全ての件数
 * @param param.topicTitle - 特集枠タイトル
 * @param param.jobList - 求人一覧
 * @returns アクセス解析の返却値
 */
export const useFeaturedJobListAnalytics = ({
  featuredId,
  totalCount,
  topicTitle,
  jobList,
}: UseFeaturedJobListAnalyticsArgs) => {
  const isAdobeScript = useAppSelector(adobeAnalyticsScriptsSelector);
  const dispatch = useAppDispatch();

  const pageName = `特集一覧(${featuredId}／${topicTitle ?? ''})`;

  useEffect(() => {
    const videoJobCount =
      jobList?.filter(({ movieId }) => !!movieId).length ?? 0;

    // AA PV計測
    if (videoJobCount > 0) {
      sc({
        channel: pageName,
        pageName,
        linkTrackEvents: 'event97',
        events: 'event97',
        linkTrackVars: 'pageName,channel,eVar56,eVar119,events',
        eVar56: `${videoJobCount}`,
        eVar119: 'prime',
      });
    }

    sc({
      channel: pageName,
      pageName,
      linkTrackVars: 'pageName,channel',
    });

    // GA PV計測
    dataLayer({
      PageName: pageName,
      Channel: pageName,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAdobeScript) {
      sc({
        eVar84: `${totalCount}`,
      });
    }
  }, [isAdobeScript, totalCount]);

  const measureOnClickApply = () => {
    tlReadyExecute(isAdobeScript, dispatch, 'Matchkeyword_ichiran_oubo', {});
    tlReadyExecute(isAdobeScript, dispatch, 'Chokusetsu Ichiran', {
      linkTrackVars: 'prop23,eVar23,events',
      linkTrackEvents: 'event8',
      prop23: '求人一覧ボタン',
      eVar23: 'D=c23',
      events: 'event8',
    });
  };

  const measureOnClickViewJobDetail = () => {
    tlReadyExecute(isAdobeScript, dispatch, 'Matchkeyword_on_etsuran', {});
  };

  const measureOnClickBookmark = (jid: string) => () => {
    tlReadyExecute(isAdobeScript, dispatch, 'kininaruClick_FeaturedJobList', {
      linkTrackVars:
        'channel,events,products,prop1,prop23,prop32,prop69,eVar23,eVar78',
      linkTrackEvents: 'event120',
      channel: pageName,
      events: 'event120',
      products: `;${jid}`,
      prop23: 'kininaru_Click',
      prop32: jid,
      eVar23: 'D=c23',
    });

    dataLayer({
      PageName: pageName,
      Channel: pageName,
      JobId: jid,
      event: 'bookmark_click',
      click_Action: 'Click ',
      click_Category: 'bookmark',
    });
  };

  return {
    measureOnClickApply,
    measureOnClickViewJobDetail,
    measureOnClickBookmark,
  };
};
